import { AuthProvider } from 'react-admin';
import { refreshAccessToken } from './tokenRefresh';

export const authProvider: AuthProvider = {
  login: async ({ username, password, email }) => {
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

    // Use email if provided, otherwise fall back to username
    const loginEmail = email || username;

    try {
      const response = await fetch(`${baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: loginEmail, password }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Login failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Fetch user details with tenants
      const userResponse = await fetch(`${baseUrl}/users/${data.user.id}`, {
        headers: {
          'Authorization': `Bearer ${data.accessToken}`,
        },
      });

      if (userResponse.ok) {
        const userWithTenants = await userResponse.json();
        localStorage.setItem('user', JSON.stringify(userWithTenants));

        // Check if user has tenants
        const hasTenants = userWithTenants.tenants && userWithTenants.tenants.length > 0;

        if (!hasTenants) {
          // Standalone user - redirect to standalone dashboard
          localStorage.setItem('isStandalone', 'true');
          window.location.href = '/standalone-dashboard';
          return Promise.resolve();
        } else {
          // User has tenants - normal flow
          localStorage.removeItem('isStandalone');
        }
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isStandalone');
    return Promise.resolve();
  },

  checkAuth: () => {
    const token = localStorage.getItem('accessToken');
    return token ? Promise.resolve() : Promise.reject();
  },

  checkError: async (error: any) => {
    // Only logout on 401 (unauthorized), not 403 (forbidden)
    if (error.status === 401) {
      console.log('checkError: Received 401, attempting to refresh token...');
      // Try to refresh the token
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        console.log('checkError: Token refreshed successfully');
        return Promise.resolve();
      } else {
        console.log('checkError: Token refresh failed, logging out...');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isStandalone');
        return Promise.reject();
      }
    }
    // For 403, just show the error but don't logout
    if (error.status === 403) {
      return Promise.resolve();
    }
    return Promise.resolve();
  },

  getIdentity: () => {
    const user = localStorage.getItem('user');
    return user ? Promise.resolve(JSON.parse(user)) : Promise.reject();
  },

  getPermissions: () => {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return Promise.resolve({
        roles: userData.roles || [],
        isSuperAdmin: userData.isSuperAdmin === true,
      });
    }
    return Promise.reject();
  },
};